import type { GetStaticPaths, GetStaticProps } from "next";
import type { Listing, TownSummary } from "@/lib/construction/types";

import Link from "next/link";

import { Meta } from "@/components/seo/Meta";
import { Header } from "@/components/sections/Header";
import { Footer } from "@/components/sections/Footer";
import { PlaceHero, DirectorySection } from "@/components/construction/PlaceHero";
import { CompanyRow } from "@/components/construction/CompanyRow";
import { TownPagination } from "@/components/construction/TownPagination";
import { DirectoryAttribution } from "@/components/construction/Attribution";
import { DIRECTORY_PATH, TOWN_PAGE_SIZE, getRegion, isRegionSlug, regionHref, townHref, townKey, townPageHref } from "@/lib/construction/places";
import { countCompanies, formatCount } from "@/lib/construction/normalise";
import { DIRECTORY_REVALIDATE } from "@/lib/construction/runtime";

type TownPageListProps = {
  town: TownSummary;
  page: number;
  pages: number;
  total: number;
  items: Listing[];
};

/**
 * Numbered pages of every company in a town. They exist so search engines can
 * reach every company page through plain links; they are noindex, follow and
 * not in the sitemap, because the town page is the one that should rank.
 */
export default function TownCompaniesPage({ town, page, pages, total, items }: TownPageListProps) {
  const region = getRegion(town.region);
  const href = townPageHref(town.region, town.slug, page);
  const townCms = town.id ? `directory_towns:${town.id}:name` : undefined;
  const TownName = () => (townCms ? <span data-mdlfy-cms={townCms}>{town.name}</span> : <>{town.name}</>);
  const first = (page - 1) * TOWN_PAGE_SIZE + 1;
  const last = first + items.length - 1;
  const crumbs = [
    { name: "Home", href: "/" },
    { name: "Construction companies", href: DIRECTORY_PATH },
    { name: region?.name ?? "Region", href: regionHref(town.region) },
    { name: town.name, href: townHref(town.region, town.slug), cms: townCms },
    { name: `Page ${page}`, href },
  ];

  return (
    <>
      <Meta
        absoluteTitle={`Construction Companies in ${town.name}, page ${page} of ${pages} | ConstructionWebDesign.uk`}
        description={`Companies ${formatCount(first)} to ${formatCount(last)} of the ${countCompanies(total)} listed in ${town.name}, ${region?.longName ?? "UK"}, from the Companies House register and OpenStreetMap.`}
        canonical={href}
        noindex
        follow
      />
      <Header />
      <main id="main">
        <PlaceHero
          crumbs={crumbs}
          eyebrow={
            <>
              <TownName />, {region?.longName ?? "UK"}
            </>
          }
          title={
            <>
              Construction Companies in <TownName />: page {page} of {pages}
            </>
          }
          answer={`This page lists companies ${formatCount(first)} to ${formatCount(last)} of the ${countCompanies(total)} in ${town.name}, with companies that have a website first, then by size and name.`}
        >
          <p className="mt-4 text-sm">
            <Link href={townHref(town.region, town.slug)} className="font-medium text-ink underline decoration-accent decoration-2 underline-offset-4">
              Back to construction companies in <TownName />
            </Link>
          </p>
          <DirectoryAttribution className="mt-6" />
        </PlaceHero>

        <DirectorySection
          id="companies"
          title={
            <>
              Which construction companies are in <TownName />?
            </>
          }
        >
          {items.length ? (
            <ul aria-label={`Construction companies in ${town.name}, page ${page}`} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((l) => (
                <CompanyRow key={l.key} listing={l} showRegion={false} />
              ))}
            </ul>
          ) : (
            <p className="rounded-2xl border border-dashed border-ink/15 bg-white p-6 text-ink-muted">No companies are listed on this page.</p>
          )}
          <TownPagination region={town.region} townSlug={town.slug} townName={town.name} total={total} current={page} />
        </DirectorySection>
      </main>
      <Footer />
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => ({ paths: [], fallback: "blocking" });

export const getStaticProps: GetStaticProps<TownPageListProps> = async ({ params }) => {
  const regionParam = typeof params?.region === "string" ? params.region : "";
  const townParam = typeof params?.town === "string" ? params.town : "";
  const pageParam = typeof params?.page === "string" ? params.page : "";
  if (!isRegionSlug(regionParam) || !townParam || !/^[1-9]\d{0,4}$/.test(pageParam)) return { notFound: true, revalidate: DIRECTORY_REVALIDATE };
  const page = Number(pageParam);

  const data = await import("@/lib/construction/data");
  const key = townKey(regionParam, townParam);
  const town = await data.getTown(key);
  if (!town || town.count === 0) return { notFound: true, revalidate: DIRECTORY_REVALIDATE };

  const listings = await data.searchListings({ towns: [key], sort: "size", limit: TOWN_PAGE_SIZE, offset: (page - 1) * TOWN_PAGE_SIZE });
  const pages = Math.max(1, Math.ceil(listings.total / TOWN_PAGE_SIZE));
  if (page > pages) return { notFound: true, revalidate: DIRECTORY_REVALIDATE };

  return {
    props: { town, page, pages, total: listings.total, items: listings.items },
    revalidate: DIRECTORY_REVALIDATE,
  };
};
